import React, { useState, useEffect } from "react";
import axios from "axios";
import LoadingSpinner from "./components/Helpers/LoadingSpinner";
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BarChart, Bar } from 'recharts';
import { Button, Card, Row, Col, Form, InputGroup, Badge } from 'react-bootstrap';
import { Spinner } from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Table as BSTable } from "react-bootstrap";
import Panel from "./components/Panel";
import { BsLightningCharge } from 'react-icons/bs';

function IOGSessions({ settings }) {
  const [monthlyData, setMonthlyData] = useState(null);
  const [yearlyTotals, setYearlyTotals] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [summary, setSummary] = useState(null);
  const [syncStarted, setSyncStarted] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  function rounder(num) { return Math.round((num + Number.EPSILON) * 100) / 100 }

  const getMonthlyData = () => {
    axios
      .get(`${process.env.REACT_APP_API ? process.env.REACT_APP_API : window.location.origin}/api/v1/iogsessions/monthly.json?year=${selectedYear}`)
      .then(({ data }) => {
        setMonthlyData(data.monthly_data);
        setYearlyTotals(data.yearly_totals);
      })
      .catch((error) => console.error(error));
  };

  const getSessions = () => {
    axios
      .get(`${process.env.REACT_APP_API ? process.env.REACT_APP_API : window.location.origin}/api/v1/iogsessions.json`)
      .then(({ data }) => {
        setSessions(data.iogsessions);
        setSummary(data.summary);
      })
      .catch((error) => console.error(error));
  };

  const syncIogSessions = () => {
    setSyncStarted(true);
    axios
      .get(`${process.env.REACT_APP_API ? process.env.REACT_APP_API : window.location.origin}/api/v1/iogsessions/sync.json`)
      .catch((error) => console.error(error))
      .then(() => {
        getMonthlyData();
        getSessions();
        setSyncStarted(false);
      });
  };

  useEffect(() => {
    getMonthlyData();
    getSessions();
  }, [selectedYear]);

  if (!monthlyData || !sessions) { return (<LoadingSpinner />) }

  const chartData = monthlyData.map(m => ({
    name: m.month_name.substring(0, 3),
    savings: m.savings,
    hours: m.hours,
    kwh: m.kwh
  }));

  const years = [];
  for (let y = 2023; y <= new Date().getFullYear(); y++) {
    years.push(y);
  }

  return (
    <div className="App">
      <Panel>
        <h1><BsLightningCharge /> Intelligent Octopus Go Sessions</h1>

        <InputGroup className="mb-3">
          <Button onClick={syncIogSessions} variant="success" disabled={syncStarted}>
            Sync IOG Sessions {syncStarted ? <Spinner size="sm" animation="border" variant="light" /> : ''}
          </Button>
          <Form.Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} style={{ maxWidth: '120px' }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </Form.Select>
          <Button onClick={getMonthlyData} variant="primary">Refresh</Button>
        </InputGroup>

        {yearlyTotals && (
          <Card bg="success" text="white" className="mb-4">
            <Card.Body>
              <Card.Title>{yearlyTotals.year} Yearly Summary</Card.Title>
              <Row>
                <Col>
                  <h4>{yearlyTotals.total_sessions}</h4>
                  <small>Total Sessions</small>
                </Col>
                <Col>
                  <h4>{rounder(yearlyTotals.total_hours)}h</h4>
                  <small>Total Hours</small>
                </Col>
                <Col>
                  <h4>{rounder(yearlyTotals.total_kwh)} kWh</h4>
                  <small>Total Charged</small>
                </Col>
                <Col>
                  <h4>£{rounder(yearlyTotals.total_savings)}</h4>
                  <small>Total Savings</small>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        <h3>Monthly Breakdown</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis yAxisId="left" orientation="left" stroke="#28a745" />
            <YAxis yAxisId="right" orientation="right" stroke="#17a2b8" />
            <Tooltip formatter={(value, name) => {
              if (name === 'savings') return [`£${rounder(value)}`, 'Savings'];
              if (name === 'hours') return [`${rounder(value)}h`, 'Hours'];
              if (name === 'kwh') return [`${rounder(value)} kWh`, 'Energy'];
              return [value, name];
            }} />
            <Legend />
            <Bar yAxisId="left" dataKey="savings" name="Savings (£)" fill="#28a745" />
            <Bar yAxisId="right" dataKey="hours" name="Hours" fill="#17a2b8" />
          </BarChart>
        </ResponsiveContainer>

        <BSTable striped bordered hover size="sm" className="mt-3">
          <thead>
            <tr>
              <th>Month</th>
              <th>Sessions</th>
              <th>Hours</th>
              <th>kWh Charged</th>
              <th>Savings</th>
            </tr>
          </thead>
          <tbody>
            {monthlyData.map((m, idx) => (
              <tr key={idx}>
                <td>{m.month_name}</td>
                <td>{m.sessions_count}</td>
                <td>{rounder(m.hours)}h</td>
                <td>{rounder(m.kwh)} kWh</td>
                <td><Badge bg="success">£{rounder(m.savings)}</Badge></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total</strong></td>
              <td><strong>{yearlyTotals?.total_sessions || 0}</strong></td>
              <td><strong>{rounder(yearlyTotals?.total_hours || 0)}h</strong></td>
              <td><strong>{rounder(yearlyTotals?.total_kwh || 0)} kWh</strong></td>
              <td><strong><Badge bg="success">£{rounder(yearlyTotals?.total_savings || 0)}</Badge></strong></td>
            </tr>
          </tfoot>
        </BSTable>

        <h3 className="mt-4">Recent Sessions (Last 30 Days)</h3>
        {summary && (
          <Card bg="info" text="white" className="mb-3">
            <Card.Body>
              <Row>
                <Col>
                  <strong>Sessions:</strong> {summary.sessions_count}
                </Col>
                <Col>
                  <strong>Hours:</strong> {rounder(summary.total_hours)}h
                </Col>
                <Col>
                  <strong>kWh:</strong> {rounder(summary.total_kwh)}
                </Col>
                <Col>
                  <strong>Savings:</strong> £{rounder(summary.total_savings)}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        <BSTable striped bordered hover size="sm">
          <thead>
            <tr>
              <th>Start</th>
              <th>End</th>
              <th>Duration</th>
              <th>kWh</th>
              <th>Off-Peak Rate</th>
              <th>Day Rate</th>
              <th>Savings</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((iog, idx) => (
              <tr key={idx}>
                <td>{new Date(iog.start).toLocaleString()}</td>
                <td>{new Date(iog.end).toLocaleString()}</td>
                <td><Badge bg="info">{rounder(iog.duration_hours)}h</Badge></td>
                <td>{rounder(iog.charge_kwh || iog.delta)} kWh</td>
                <td>{iog.offpeak_rate ? `${rounder(iog.offpeak_rate)}p` : 'N/A'}</td>
                <td>{iog.day_rate ? `${rounder(iog.day_rate)}p` : 'N/A'}</td>
                <td><Badge bg="success">£{rounder(iog.savings || 0)}</Badge></td>
                <td>{iog.source || 'N/A'}</td>
              </tr>
            ))}
          </tbody>
        </BSTable>
      </Panel>
    </div>
  );
}

export default IOGSessions;
